import { RetailerNetworkPartialFragment } from '@occc/graphql';
import { LazyAutocompleteNotMultiple } from '@occc/ui-design-system';

import {
  makeRetailerNetworkFilter,
  mapRetailerNetworksResponseToRawArray,
  mapRetailerNetworkToAutocompleteOption,
} from './retailer-network-autocomplete.helpers';

import { useGetRetailerNetworksForUserLazyQuery } from '../../data-access/queries';

const RETAILER_NETWORKS_PAGE_SIZE = 50;

// The engine only reads uuid/name from the current value to render the selected
// option, so callers that only persist those two fields need not carry the full
// fragment (e.g. a filter that stores just { uuid, name }).
export type RetailerNetworkIdentity = Pick<RetailerNetworkPartialFragment, 'uuid' | 'name'>;

type RetailerNetworkSingleSelectFieldProps = {
  value: RetailerNetworkIdentity | null;
  onChange: (value: RetailerNetworkIdentity | null) => void;
  label?: string;
  placeholder?: string;
  first?: number;
  size?: 'small' | 'medium';
  // When false, the field is not rendered so the lazy query never fires. Lets a
  // caller inside a `keepMounted` surface (e.g. a closed filter popover) avoid
  // fetching until the field is actually shown. Defaults to true.
  isOpen?: boolean;
};

export const RetailerNetworkSingleSelectField = ({
  value,
  onChange,
  label = 'Retailer Network',
  placeholder = 'Type to search',
  first = RETAILER_NETWORKS_PAGE_SIZE,
  size = 'small',
  isOpen = true,
}: RetailerNetworkSingleSelectFieldProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <LazyAutocompleteNotMultiple<
      RetailerNetworkPartialFragment,
      ReturnType<typeof useGetRetailerNetworksForUserLazyQuery>['response']
    >
      // Only uuid/name are read from value; a persisted identity is sufficient.
      value={value as RetailerNetworkPartialFragment | null}
      onChange={onChange}
      label={label}
      placeholder={placeholder}
      first={first}
      size={size}
      inputProps={{ size, margin: size === 'small' ? 'none' : 'normal' }}
      makeQueryFilter={(search: string) => makeRetailerNetworkFilter({ search })}
      useLazyQuery={useGetRetailerNetworksForUserLazyQuery}
      mapQueryResponseToRawArray={mapRetailerNetworksResponseToRawArray}
      mapRawResponseItemToAutocompleteOption={mapRetailerNetworkToAutocompleteOption}
    />
  );
};
